export const CELL_SCOPE_HEADER = 'X-Cell-Id';
export const CELL_SCOPE_STORAGE_KEY = 'platform.cellScope';
export const DEFAULT_CELL_SCOPE = 'shared';

const CELL_ID_PATTERN = /^[a-z0-9][a-z0-9-_]{0,62}$/;

const normalizeCellId = (value: unknown): string => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (!normalized) return DEFAULT_CELL_SCOPE;
  return CELL_ID_PATTERN.test(normalized) ? normalized : DEFAULT_CELL_SCOPE;
};

const parseEnvCellIds = (): string[] => {
  const raw = String(
    process.env.VITE_AVAILABLE_CELL_IDS || process.env.REACT_APP_AVAILABLE_CELL_IDS || ''
  );
  if (!raw.trim()) return [];
  return raw
    .split(',')
    .map((item) => normalizeCellId(item))
    .filter(Boolean);
};

export const getAvailableCellIds = (): string[] => {
  const configured = parseEnvCellIds();
  const deduped = new Set<string>([DEFAULT_CELL_SCOPE, ...configured]);
  return Array.from(deduped);
};

export const readCellScope = (): string => {
  if (typeof window === 'undefined') return DEFAULT_CELL_SCOPE;
  try {
    return normalizeCellId(window.localStorage.getItem(CELL_SCOPE_STORAGE_KEY));
  } catch (_) {
    return DEFAULT_CELL_SCOPE;
  }
};

export const writeCellScope = (value: string): string => {
  const normalized = normalizeCellId(value);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(CELL_SCOPE_STORAGE_KEY, normalized);
    } catch (_) {}
  }
  return normalized;
};

const isApiRequest = (input: RequestInfo | URL): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const raw =
      input instanceof Request
        ? input.url
        : input instanceof URL
          ? input.toString()
          : String(input || '');
    const resolved = new URL(raw, window.location.origin);
    if (resolved.origin !== window.location.origin) return false;
    return resolved.pathname.startsWith('/api/');
  } catch (_) {
    return false;
  }
};

const buildScopedHeaders = (
  input: RequestInfo | URL,
  initHeaders: HeadersInit | undefined,
  cellId: string
): Headers => {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  if (initHeaders) {
    const additional = new Headers(initHeaders);
    additional.forEach((value, key) => headers.set(key, value));
  }
  headers.set(CELL_SCOPE_HEADER, normalizeCellId(cellId));
  return headers;
};

export const applyCellScopeToRequest = (
  input: RequestInfo | URL,
  init?: RequestInit,
  cellId: string = readCellScope()
): [RequestInfo | URL, RequestInit | undefined] => {
  if (!isApiRequest(input)) {
    return [input, init];
  }
  const scopedHeaders = buildScopedHeaders(input, init?.headers, cellId);

  if (input instanceof Request) {
    const nextRequest = new Request(input, {
      ...init,
      headers: scopedHeaders,
    });
    return [nextRequest, undefined];
  }

  return [
    input,
    {
      ...(init || {}),
      headers: scopedHeaders,
    },
  ];
};

export const normalizeCellScopeValue = normalizeCellId;
